import React, { useState, useEffect, useRef, useCallback } from 'react';
import styled, { keyframes } from 'styled-components';
import {
  MdChat, MdClose, MdSend, MdAdd, MdDelete, MdEdit, MdCheck,
  MdSmartToy, MdPerson, MdHistory, MdArrowBack
} from 'react-icons/md';
import { chatAPI } from '../services/api';

/* ── Animations ── */
const fadeIn = keyframes`from { opacity: 0; transform: translateY(12px); } to { opacity: 1; transform: translateY(0); }`;
const spin = keyframes`from { transform: rotate(0deg); } to { transform: rotate(360deg); }`;
const pulse = keyframes`0%,100% { opacity: 1; } 50% { opacity: 0.4; }`;

/* ── FAB button ── */
const Fab = styled.button`
  position: fixed; bottom: 28px; right: 28px; z-index: 9998;
  width: 56px; height: 56px; border-radius: 50%; border: none;
  background: linear-gradient(135deg, ${p => p.theme.colors.primary}, ${p => p.theme.colors.primaryDark});
  color: white; cursor: pointer; display: flex; align-items: center; justify-content: center;
  box-shadow: 0 4px 20px ${p => p.theme.colors.primary}55;
  transition: all 0.2s;
  svg { font-size: 1.6rem; }
  &:hover { transform: scale(1.08); box-shadow: 0 6px 24px ${p => p.theme.colors.primary}70; }
  @media (max-width: 480px) { bottom: 20px; right: 16px; }
  @media print { display: none; }
`;

/* ── Panel ── */
const Panel = styled.div`
  position: fixed; bottom: 96px; right: 28px; z-index: 9999;
  width: min(420px, calc(100vw - 56px));
  height: min(620px, calc(100vh - 130px));
  background: ${p => p.theme.colors.backgroundLight};
  border-radius: ${p => p.theme.borderRadius.lg};
  box-shadow: 0 8px 40px rgba(0,0,0,0.18);
  display: flex; flex-direction: column; overflow: hidden;
  animation: ${fadeIn} 0.2s ease;
  @media (max-width: 640px) {
    width: calc(100vw - 32px);
    height: calc(100vh - 130px);
    right: 16px;
    bottom: 86px;
  }
  @media (max-width: 480px) {
    inset: 0;
    width: 100%;
    height: 100%;
    border-radius: 0;
    bottom: 0;
  }
  @media print { display: none; }
`;

/* ── Header ── */
const Header = styled.div`
  display: flex; align-items: center; gap: 10px;
  padding: 14px 16px;
  background: linear-gradient(135deg, ${p => p.theme.colors.primary}, ${p => p.theme.colors.primaryDark});
  color: white;
`;
const HeaderIcon = styled.div`
  width: 34px; height: 34px; border-radius: 50%;
  background: rgba(255,255,255,0.2);
  display: flex; align-items: center; justify-content: center;
  svg { font-size: 1.2rem; }
`;
const HeaderTitle = styled.div`flex: 1;`;
const HeaderName = styled.div`font-size: 14px; font-weight: 700;`;
const HeaderSub = styled.div`font-size: 11px; opacity: 0.8;`;
const HeaderBtn = styled.button`
  background: none; border: none; color: white; cursor: pointer; padding: 4px;
  border-radius: 6px; display: flex; align-items: center; opacity: 0.8;
  svg { font-size: 1.2rem; }
  &:hover { background: rgba(255,255,255,0.15); opacity: 1; }
`;

/* ── Sidebar (historial) ── */
const Sidebar = styled.div`
  flex-shrink: 0; width: 100%; display: flex; flex-direction: column;
  border-bottom: 1px solid ${p => p.theme.colors.border};
`;
const SidebarHeader = styled.div`
  display: flex; align-items: center; justify-content: space-between;
  padding: 10px 14px;
  border-bottom: 1px solid ${p => p.theme.colors.border};
  font-size: 12px; font-weight: 700; color: ${p => p.theme.colors.textSecondary};
  text-transform: uppercase; letter-spacing: 0.05em;
`;
const NewChatBtn = styled.button`
  display: flex; align-items: center; gap: 5px; padding: 5px 10px;
  background: ${p => p.theme.colors.primary}15; color: ${p => p.theme.colors.primary};
  border: 1px solid ${p => p.theme.colors.primary}40; border-radius: 6px;
  font-size: 12px; font-weight: 600; cursor: pointer;
  font-family: ${p => p.theme.fonts.primary};
  svg { font-size: 1rem; }
  &:hover { background: ${p => p.theme.colors.primary}25; }
`;
const SessionList = styled.div`
  max-height: 160px; overflow-y: auto;
  @media (max-width: 480px) { max-height: 220px; }
`;
const SessionItem = styled.div`
  display: flex; align-items: center; gap: 8px; padding: 8px 14px;
  cursor: pointer; border-bottom: 1px solid ${p => p.theme.colors.borderLight};
  background: ${p => p.active ? p.theme.colors.primary + '10' : 'transparent'};
  &:last-child { border-bottom: none; }
  &:hover { background: ${p => p.active ? p.theme.colors.primary + '10' : p.theme.colors.background}; }
`;
const SessionInfo = styled.div`flex: 1; min-width: 0;`;
const SessionTitle = styled.div`
  font-size: 12px; font-weight: 600; color: ${p => p.theme.colors.textPrimary};
  white-space: nowrap; overflow: hidden; text-overflow: ellipsis;
`;
const SessionDate = styled.div`font-size: 10px; color: ${p => p.theme.colors.textLight}; margin-top: 1px;`;
const SessionActions = styled.div`display: flex; gap: 2px; opacity: 0; transition: opacity 0.15s;
  ${SessionItem}:hover & { opacity: 1; }
`;
const SessBtn = styled.button`
  background: none; border: none; cursor: pointer; padding: 3px;
  color: ${p => p.danger ? p.theme.colors.danger : p.theme.colors.textSecondary};
  border-radius: 4px; display: flex; align-items: center;
  svg { font-size: 0.9rem; }
  &:hover { background: ${p => p.danger ? p.theme.colors.danger + '15' : p.theme.colors.border}; }
`;

/* ── Messages area ── */
const MessagesArea = styled.div`
  flex: 1; overflow-y: auto; padding: 14px;
  display: flex; flex-direction: column; gap: 12px;
  &::-webkit-scrollbar { width: 4px; }
  &::-webkit-scrollbar-thumb { background: ${p => p.theme.colors.border}; border-radius: 2px; }
`;
const MsgRow = styled.div`
  display: flex; gap: 8px;
  flex-direction: ${p => p.user ? 'row-reverse' : 'row'};
  align-items: flex-end;
`;
const Avatar = styled.div`
  width: 28px; height: 28px; border-radius: 50%; flex-shrink: 0;
  display: flex; align-items: center; justify-content: center;
  background: ${p => p.user
    ? p.theme.colors.primary + '20'
    : 'linear-gradient(135deg, ' + p.theme.colors.primary + ', ' + p.theme.colors.primaryDark + ')'};
  color: ${p => p.user ? p.theme.colors.primary : 'white'};
  svg { font-size: 0.9rem; }
`;
const Bubble = styled.div`
  max-width: 78%; padding: 10px 13px;
  border-radius: ${p => p.user ? '16px 16px 4px 16px' : '16px 16px 16px 4px'};
  background: ${p => p.user
    ? 'linear-gradient(135deg, ' + p.theme.colors.primary + ', ' + p.theme.colors.primaryDark + ')'
    : p.theme.colors.background};
  color: ${p => p.user ? 'white' : p.theme.colors.textPrimary};
  font-size: 13px; line-height: 1.5;
  border: ${p => p.user ? 'none' : '1px solid ' + p.theme.colors.border};
  white-space: pre-wrap; word-break: break-word;
`;
const TypingDot = styled.span`
  display: inline-block; width: 6px; height: 6px; border-radius: 50%;
  background: ${p => p.theme.colors.textSecondary};
  animation: ${pulse} 1.2s ease ${p => p.delay || '0s'} infinite;
  margin: 0 2px;
`;
const EmptyState = styled.div`
  flex: 1; display: flex; flex-direction: column; align-items: center; justify-content: center;
  gap: 8px; color: ${p => p.theme.colors.textLight}; text-align: center; padding: 20px;
  svg { font-size: 2.5rem; opacity: 0.3; }
  p { font-size: 13px; margin: 0; }
  small { font-size: 11px; opacity: 0.7; }
`;
const SuggestionChips = styled.div`
  display: flex; flex-wrap: wrap; gap: 6px; margin-top: 12px; justify-content: center;
  @media (max-width: 480px) { gap: 8px; }
`;
const Chip = styled.button`
  padding: 5px 11px; border: 1px solid ${p => p.theme.colors.border};
  border-radius: 99px; background: ${p => p.theme.colors.backgroundLight};
  color: ${p => p.theme.colors.textSecondary}; font-size: 11px;
  font-family: ${p => p.theme.fonts.primary}; cursor: pointer; transition: all 0.15s;
  &:hover { border-color: ${p => p.theme.colors.primary}; color: ${p => p.theme.colors.primary}; }
  @media (max-width: 480px) { padding: 7px 13px; font-size: 12px; }
`;

/* ── Input area ── */
const InputArea = styled.div`
  padding: 10px 12px;
  border-top: 1px solid ${p => p.theme.colors.border};
  display: flex; gap: 8px; align-items: flex-end;
  @media (max-width: 480px) {
    padding: 10px 12px 16px;
  }
`;
const TextInput = styled.textarea`
  flex: 1; padding: 9px 12px; resize: none; min-height: 38px; max-height: 100px;
  border: 1px solid ${p => p.theme.colors.border};
  border-radius: ${p => p.theme.borderRadius.md};
  font-size: 13px; font-family: ${p => p.theme.fonts.primary};
  background: ${p => p.theme.colors.background}; color: ${p => p.theme.colors.textPrimary};
  line-height: 1.4; overflow-y: auto;
  &:focus { outline: none; border-color: ${p => p.theme.colors.primary}; }
  &::placeholder { color: ${p => p.theme.colors.textLight}; }
  @media (max-width: 480px) { font-size: 14px; min-height: 42px; }
`;
const SendButton = styled.button`
  width: 38px; height: 38px; border-radius: ${p => p.theme.borderRadius.md};
  border: none; cursor: pointer; flex-shrink: 0;
  display: flex; align-items: center; justify-content: center;
  background: ${p => p.disabled
    ? p.theme.colors.border
    : `linear-gradient(135deg, ${p.theme.colors.primary}, ${p.theme.colors.primaryDark})`};
  color: white; transition: all 0.15s;
  svg { font-size: 1.1rem; }
  &:hover:not(:disabled) { transform: scale(1.05); }
  @media (max-width: 480px) { width: 42px; height: 42px; }
`;
const Spinner = styled.div`
  width: 16px; height: 16px; border: 2px solid rgba(255,255,255,0.3);
  border-top-color: white; border-radius: 50%;
  animation: ${spin} 0.7s linear infinite;
`;

/* ── Rename input inline ── */
const RenameInput = styled.input`
  flex: 1; font-size: 12px; padding: 2px 6px;
  border: 1px solid ${p => p.theme.colors.primary}; border-radius: 4px;
  background: ${p => p.theme.colors.backgroundLight}; color: ${p => p.theme.colors.textPrimary};
  font-family: ${p => p.theme.fonts.primary};
  &:focus { outline: none; }
`;

const fmtDate = d => {
  if (!d) return '';
  const date = new Date(d);
  const now = new Date();
  const diffMs = now - date;
  const diffMins = Math.floor(diffMs / 60000);
  if (diffMins < 1) return 'ahora';
  if (diffMins < 60) return `${diffMins}m`;
  const diffH = Math.floor(diffMins / 60);
  if (diffH < 24) return `${diffH}h`;
  return date.toLocaleDateString('es-MX', { day: '2-digit', month: 'short' });
};

const SUGGESTIONS = [
  '¿Cuánto debo cobrar en total?',
  '¿Quiénes son mis clientes con más deuda?',
  '¿Qué recibos vencen esta semana?',
  '¿Cuánto facturé este mes?',
  '¿Qué hay en el inventario?',
];

/* ═══════════════════════════════════════════════════════════════════════════ */
const ChatWidget = () => {
  const [open, setOpen] = useState(false);
  const [showHistory, setShowHistory] = useState(false);
  const [sessions, setSessions] = useState([]);
  const [activeSession, setActiveSession] = useState(null); // { id, titulo, messages: [] }
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [renamingId, setRenamingId] = useState(null);
  const [renameValue, setRenameValue] = useState('');
  const messagesEndRef = useRef(null);
  const textareaRef = useRef(null);

  const scrollToBottom = () => messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });

  useEffect(() => { if (open) scrollToBottom(); }, [activeSession?.messages, open]);

  const loadSessions = useCallback(async () => {
    try {
      const r = await chatAPI.getSessions();
      setSessions(r.data.data || []);
    } catch {}
  }, []);

  useEffect(() => { if (open) loadSessions(); }, [open, loadSessions]);

  const openSession = async (sessionId) => {
    try {
      const r = await chatAPI.getSession(sessionId);
      const s = r.data.data;
      setActiveSession({ id: s.id, titulo: s.titulo, messages: s.messages || [] });
      setShowHistory(false);
    } catch {}
  };

  const startNewChat = () => {
    setActiveSession(null);
    setShowHistory(false);
    setInput('');
  };

  const handleDelete = async (e, id) => {
    e.stopPropagation();
    try {
      await chatAPI.deleteSession(id);
      setSessions(prev => prev.filter(s => s.id !== id));
      if (activeSession?.id === id) setActiveSession(null);
    } catch {}
  };

  const startRename = (e, session) => {
    e.stopPropagation();
    setRenamingId(session.id);
    setRenameValue(session.titulo);
  };

  const confirmRename = async (id) => {
    if (!renameValue.trim()) { setRenamingId(null); return; }
    try {
      await chatAPI.renameSession(id, renameValue.trim());
      setSessions(prev => prev.map(s => s.id === id ? { ...s, titulo: renameValue.trim() } : s));
      if (activeSession?.id === id) setActiveSession(prev => ({ ...prev, titulo: renameValue.trim() }));
    } catch {}
    setRenamingId(null);
  };

  const send = async (text) => {
    const msg = (text || input).trim();
    if (!msg || loading) return;
    setInput('');

    const userMsg = { id: Date.now(), role: 'user', content: msg };
    const currentMessages = activeSession ? [...activeSession.messages, userMsg] : [userMsg];

    if (activeSession) {
      setActiveSession(prev => ({ ...prev, messages: currentMessages }));
    } else {
      setActiveSession({ id: null, titulo: msg.substring(0, 60), messages: currentMessages });
    }

    setLoading(true);
    try {
      const r = await chatAPI.sendMessage({
        session_id: activeSession?.id || null,
        message: msg,
      });
      const { session_id, message } = r.data.data;
      const assistantMsg = { id: Date.now() + 1, role: 'assistant', content: message };

      setActiveSession(prev => ({
        id: session_id,
        titulo: prev?.titulo || msg.substring(0, 60),
        messages: [...(prev?.messages || []), assistantMsg],
      }));

      // Refrescar lista de sesiones
      loadSessions();
    } catch (err) {
      const errMsg = err.response?.data?.message || 'Error al conectar con el asistente';
      setActiveSession(prev => ({
        ...prev,
        messages: [...(prev?.messages || []), { id: Date.now() + 1, role: 'assistant', content: `⚠️ ${errMsg}` }],
      }));
    } finally {
      setLoading(false);
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); send(); }
  };

  const hasMessages = activeSession?.messages?.length > 0;

  return (
    <>
      <Fab onClick={() => setOpen(o => !o)} title="Asistente IA">
        {open ? <MdClose /> : <MdChat />}
      </Fab>

      {open && (
        <Panel>
          {/* Header */}
          <Header>
            <HeaderIcon><MdSmartToy /></HeaderIcon>
            <HeaderTitle>
              <HeaderName>
                {activeSession?.titulo
                  ? activeSession.titulo.length > 28 ? activeSession.titulo.substring(0, 28) + '…' : activeSession.titulo
                  : 'Asistente IA'}
              </HeaderName>
              <HeaderSub>Acceso completo al CRM</HeaderSub>
            </HeaderTitle>
            <HeaderBtn onClick={() => setShowHistory(h => !h)} title="Historial">
              <MdHistory />
            </HeaderBtn>
            {activeSession && (
              <HeaderBtn onClick={startNewChat} title="Nueva conversación">
                <MdAdd />
              </HeaderBtn>
            )}
            <HeaderBtn onClick={() => setOpen(false)} title="Cerrar">
              <MdClose />
            </HeaderBtn>
          </Header>

          {/* Historial desplegable */}
          {showHistory && (
            <Sidebar>
              <SidebarHeader>
                Conversaciones
                <NewChatBtn onClick={startNewChat}><MdAdd /> Nueva</NewChatBtn>
              </SidebarHeader>
              <SessionList>
                {sessions.length === 0 && (
                  <div style={{ padding: '12px 14px', fontSize: 12, color: '#aaa', textAlign: 'center' }}>
                    Sin conversaciones aún
                  </div>
                )}
                {sessions.map(s => (
                  <SessionItem key={s.id} active={activeSession?.id === s.id} onClick={() => openSession(s.id)}>
                    {renamingId === s.id ? (
                      <>
                        <RenameInput
                          value={renameValue}
                          onChange={e => setRenameValue(e.target.value)}
                          onKeyDown={e => { if (e.key === 'Enter') confirmRename(s.id); if (e.key === 'Escape') setRenamingId(null); }}
                          autoFocus
                          onClick={e => e.stopPropagation()}
                        />
                        <SessBtn onClick={e => { e.stopPropagation(); confirmRename(s.id); }}><MdCheck /></SessBtn>
                      </>
                    ) : (
                      <>
                        <SessionInfo>
                          <SessionTitle>{s.titulo}</SessionTitle>
                          <SessionDate>{fmtDate(s.updated_at)}</SessionDate>
                        </SessionInfo>
                        <SessionActions>
                          <SessBtn onClick={e => startRename(e, s)}><MdEdit /></SessBtn>
                          <SessBtn danger onClick={e => handleDelete(e, s.id)}><MdDelete /></SessBtn>
                        </SessionActions>
                      </>
                    )}
                  </SessionItem>
                ))}
              </SessionList>
            </Sidebar>
          )}

          {/* Mensajes */}
          <MessagesArea>
            {!hasMessages ? (
              <EmptyState>
                <MdSmartToy />
                <p>¿En qué puedo ayudarte?</p>
                <small>Pregunta sobre clientes, cobros, inventario, cotizaciones o cualquier dato del sistema.</small>
                <SuggestionChips>
                  {SUGGESTIONS.map(s => (
                    <Chip key={s} onClick={() => send(s)}>{s}</Chip>
                  ))}
                </SuggestionChips>
              </EmptyState>
            ) : (
              activeSession.messages.map(msg => (
                <MsgRow key={msg.id} user={msg.role === 'user'}>
                  <Avatar user={msg.role === 'user'}>
                    {msg.role === 'user' ? <MdPerson /> : <MdSmartToy />}
                  </Avatar>
                  <Bubble user={msg.role === 'user'}>{msg.content}</Bubble>
                </MsgRow>
              ))
            )}

            {loading && (
              <MsgRow>
                <Avatar><MdSmartToy /></Avatar>
                <Bubble>
                  <TypingDot delay="0s" />
                  <TypingDot delay="0.2s" />
                  <TypingDot delay="0.4s" />
                </Bubble>
              </MsgRow>
            )}
            <div ref={messagesEndRef} />
          </MessagesArea>

          {/* Input */}
          <InputArea>
            <TextInput
              ref={textareaRef}
              placeholder="Escribe una pregunta..."
              value={input}
              onChange={e => setInput(e.target.value)}
              onKeyDown={handleKeyDown}
              rows={1}
            />
            <SendButton onClick={() => send()} disabled={loading || !input.trim()}>
              {loading ? <Spinner /> : <MdSend />}
            </SendButton>
          </InputArea>
        </Panel>
      )}
    </>
  );
};

export default ChatWidget;
